import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiGeneralParams, IApiResponse } from "@/types";
import type {
  IMyDigitalIdProfile,
  IUpdateDigitalIdContactSubmissionPayload,
  IUpsertDigitalIdPayload,
  TGetDigitalIdSubmissionsResponse,
} from "@/types/digital-id.interface";

export const msDigitalIdApi = createApi({
  reducerPath: "msDigitalIdApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["MyDigitalId", "DigitalIdSubmissions"],
  endpoints: (builder) => ({
    getMyDigitalId: builder.query<IApiResponse<IMyDigitalIdProfile>, void>({
      query: () => ({ url: APIEndpoints.DigitalId.Me }),
      providesTags: ["MyDigitalId"],
    }),
    upsertDigitalId: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      IUpsertDigitalIdPayload
    >({
      query: (body) => ({
        url: APIEndpoints.DigitalId.Me,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MyDigitalId"],
    }),
    uploadDigitalIdAvatar: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      { file: File }
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: APIEndpoints.DigitalId.Avatar, method: "POST", body: formData };
      },
      invalidatesTags: ["MyDigitalId"],
    }),
    uploadDigitalIdCover: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      { file: File }
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: APIEndpoints.DigitalId.Cover, method: "POST", body: formData };
      },
      invalidatesTags: ["MyDigitalId"],
    }),
    removeDigitalIdAvatar: builder.mutation<IApiResponse<IMyDigitalIdProfile>, void>({
      query: () => ({ url: APIEndpoints.DigitalId.Avatar, method: "DELETE" }),
      invalidatesTags: ["MyDigitalId"],
    }),
    removeDigitalIdCover: builder.mutation<IApiResponse<IMyDigitalIdProfile>, void>({
      query: () => ({ url: APIEndpoints.DigitalId.Cover, method: "DELETE" }),
      invalidatesTags: ["MyDigitalId"],
    }),
    addDigitalIdMedia: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      { file: File; caption?: string }
    >({
      query: ({ file, caption }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (caption) formData.append("caption", caption);
        return { url: APIEndpoints.DigitalId.Media, method: "POST", body: formData };
      },
      invalidatesTags: ["MyDigitalId"],
    }),
    uploadDigitalIdProjectImage: builder.mutation<
      IApiResponse<{ fileId: string; url: string }>,
      { file: File }
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: APIEndpoints.DigitalId.ProjectImage,
          method: "POST",
          body: formData,
        };
      },
      // The fileId only persists via the profile upsert; nothing to invalidate.
    }),
    updateDigitalIdMediaCaption: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      { id: number; caption: string }
    >({
      query: ({ id, caption }) => ({
        url: APIEndpoints.DigitalId.MediaById(id),
        method: "PATCH",
        body: { caption },
      }),
      invalidatesTags: ["MyDigitalId"],
    }),
    deleteDigitalIdMedia: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: APIEndpoints.DigitalId.MediaById(id),
        method: "DELETE",
      }),
      invalidatesTags: ["MyDigitalId"],
    }),
    reorderDigitalIdMedia: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      { ids: number[] }
    >({
      query: (body) => ({
        url: APIEndpoints.DigitalId.MediaReorder,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["MyDigitalId"],
    }),
    publishDigitalId: builder.mutation<
      IApiResponse<IMyDigitalIdProfile>,
      { isPublished: boolean }
    >({
      query: (body) => ({
        url: APIEndpoints.DigitalId.Publish,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MyDigitalId"],
    }),
    getDigitalIdSubmissions: builder.query<
      TGetDigitalIdSubmissionsResponse,
      IApiGeneralParams | void
    >({
      query: (params) => ({
        url: APIEndpoints.DigitalId.Submissions,
        params: params ?? {},
      }),
      providesTags: ["DigitalIdSubmissions"],
    }),
    updateDigitalIdSubmission: builder.mutation<
      IApiResponse<unknown>,
      { id: number } & IUpdateDigitalIdContactSubmissionPayload
    >({
      query: ({ id, ...body }) => ({
        url: APIEndpoints.DigitalId.SubmissionById(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["DigitalIdSubmissions"],
    }),
  }),
});

export const {
  useGetMyDigitalIdQuery,
  useUpsertDigitalIdMutation,
  useUploadDigitalIdAvatarMutation,
  useUploadDigitalIdCoverMutation,
  useRemoveDigitalIdAvatarMutation,
  useRemoveDigitalIdCoverMutation,
  useAddDigitalIdMediaMutation,
  useUpdateDigitalIdMediaCaptionMutation,
  useUploadDigitalIdProjectImageMutation,
  useDeleteDigitalIdMediaMutation,
  useReorderDigitalIdMediaMutation,
  usePublishDigitalIdMutation,
  useGetDigitalIdSubmissionsQuery,
  useUpdateDigitalIdSubmissionMutation,
} = msDigitalIdApi;
